"use client";

import { useTranslations } from "next-intl";
import { Logo } from "@repo/ui/components/ui/header/logo";
import Link from "next/link";
import { Separator } from "@repo/ui/components/ui/separator";
import Icon, { IconName } from "@repo/ui/components/ui/icon/icon";
import { usePathname } from "next/navigation";
import LanguageSwitcher from "@components/language-switcher";
import { useRegistration } from "@/hooks/useRegistration";
import { cn } from "@repo/ui/lib/utils";

export function Footer() {
  const t = useTranslations("Footer");
  const pathname = usePathname();
  const isTarget = pathname.split("/").includes("returns");
  const isLegal = pathname.split("/").includes("legal");
  const keys = ["discover_more", "who_we_are", "partner", "help"] as const;
  const paymentIcons = [
    "Visa",
    "Maestro",
    //"Amex",
    "ApplePay",
    "GooglePay",
    "Paypal",
    //"AmazonPay",
  ] as Array<IconName>;
  const socialIcons = [
    //"Facebook",
    { icon: "Instagram", link: "https://www.instagram.com/ipriceit" },
    {
      icon: "Facebook",
      link: "https://www.facebook.com/profile.php?id=61577330823182",
    },
    { icon: "Youtube", link: "https://www.youtube.com/@Ipriceit" },
    //"Twitter",
    //"TikTok",
    { icon: "Linkedin", link: "https://www.linkedin.com/company/ipriceit" },
  ] as Array<{ icon: IconName; link: string }>;
  const { signInAction } = useRegistration();

  return (
    <>
      {isLegal && (
        <p className="px-[80px] py-[60px] max-md:px-[40px] max-md:py-[40px] bg-secondary-gray1/40 dark:bg-secondary-black/70 max-md:text-[14px] mt-[20px]">
          {t.rich("copyright", {
            br: () => <br />,
          })}
        </p>
      )}
      {!isTarget && (
        <div className="px-[40px] py-[80px] bg-secondary-gray2 dark:bg-secondary-black max-md:px-[24px] max-md:py-[40px]">
          <h4 className="text-[30px] max-md:text-[20px] max-md:font-medium text-center mb-[40px] max-md:mb-[20px]">
            {t("payment_method")}
          </h4>
          <div className="flex gap-[50px] max-md:gap-[36px] justify-center max-md:flex-wrap">
            {paymentIcons.map((icon) => (
              <Icon
                key={icon}
                icon={icon}
                className={cn(
                  "w-[60px] max-md:w-[46px] *:fill-secondary-black dark:*:fill-primary-white",
                  { "w-[38px] max-md:w-[30px]": icon === "Paypal" },
                )}
              />
            ))}
          </div>
        </div>
      )}
      <div className="p-[40px] max-md:p-[24px]">
        <div className="grid grid-flow-col auto-cols-auto max-md:grid-flow-row max-md:auto-rows-auto">
          <div className="flex flex-col gap-5 md:max-w-[390px]">
            <div className="max-md:hidden">
              <Logo />
            </div>
            <div className="max-md:mx-auto flex gap-[50px] md:mt-[20px]">
              {socialIcons.map((icon, index) => (
                <Link href={`${icon.link}`} key={index} target="_blank">
                  <Icon
                    key={icon.icon}
                    icon={icon.icon}
                    className="h-[32px] max-md:w-[32px] *:fill-secondary-black dark:*:fill-primary-white"
                  />
                </Link>
              ))}
            </div>
            {/*<p className="max-md:text-[14px] mt-[20px]">*/}
            {/*  {t.rich("copyright", {*/}
            {/*    br: () => <br />,*/}
            {/*  })}*/}
            {/*</p>*/}
          </div>
          <Separator className="my-[24px] md:hidden" />
          {keys.map((key) => (
            <div key={key}>
              <p className="font-semibold text-[20px] max-md:text-[18px]">
                {t(`cols.${key}.title`)}
              </p>
              {t(`cols.${key}.items`)
                .split(",")
                .map((item) => {
                  const [title, link] = item.split("|") as any;
                  const isRegister = link === "register";
                  if (isRegister) {
                    return (
                      <div
                        key={title}
                        onClick={() => signInAction(true)}
                        className="cursor-pointer mt-[20px] max-md:mt-[10px] underline text-secondary-gray4 dark:text-secondary-gray1 font-normal text-[16px] max-md:text-[12px]"
                      >
                        {title}
                      </div>
                    );
                  }

                  return (
                    <Link href={`${link}`} key={title}>
                      <div className="mt-[20px] max-md:mt-[10px] underline text-secondary-gray4 dark:text-secondary-gray1 font-normal text-[16px] max-md:text-[12px]">
                        {title}
                      </div>
                    </Link>
                  );
                })}
              <Separator className="my-[24px] md:hidden" />
            </div>
          ))}
        </div>
        <Separator className="my-[40px] max-md:hidden" />
        <div className="flex items-center justify-between max-md:pb-[20px] md:ml-[100px]">
          <div className="max-md:hidden">
            <LanguageSwitcher />
          </div>
          <div className="flex gap-10 max-md:gap-0 max-md:flex-col">
            {t("legal_privacy")
              .split(",")
              .map((item) => {
                const [title, link] = item.split("|") as any;
                return (
                  <Link href={`/${link}`} key={title}>
                    <div className="max-md:mt-[10px] underline text-secondary-gray4 dark:text-secondary-gray1 font-normal text-[16px] max-md:text-[12px]">
                      {title}
                    </div>
                  </Link>
                );
              })}
          </div>
        </div>
      </div>
    </>
  );
}
